import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Determins if the current date is between the start and end date inclusively.
 * @param {string} startDate  startDate should be in the format of 'YYYY-MM-DD'
 * @param {string} endDate endDate should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns {boolean} true if the current date is between the start and end date inclusively, false otherwise
 */
export const isDateBetween = (startDate, endDate, precision = "day") => {
  if (!startDate || !endDate) return false;
  return dayjs().isBetween(startDate, endDate, precision, "[]");
};

/**
 * Determins if the current date is before the supplied date.
 * @param {string} date Date should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns true if the current date is before the supplied date, false otherwise
 */
export const isDateBefore = (date, precision = "day") => {
  if (!date) return false;
  return dayjs().isBefore(date, precision);
};

/**
 * Determins if the current date is on or before the supplied date.
 * @param {string} date Date should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns true if the current date is on or before the supplied date, false otherwise
 */
export const isDateOnOrBefore = (date, precision = "day") => {
  if (!date) return false;
  return dayjs().isSameOrBefore(date, precision);
};

/**
 * Determins if the current date is after the supplied date.
 * @param {string} date Date should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns true if the current date is after the supplied date, false otherwise
 */
export const isDateAfter = (date, precision = "day") => {
  if (!date) return false;
  return dayjs().isAfter(date, precision);
};

/**
 * Determins if the current date is on or after the supplied date.
 * @param {string} date Date should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns true if the current date is on or after the supplied date, false otherwise
 */
export const isDateOnOrAfter = (date, precision = "day") => {
  if (!date) return false;
  return dayjs().isSameOrAfter(date, precision);
};

/**
 * Determins if the current date is within the supplied dates.
 * If only one date is supplied, it will check if the current date is before/after that date, depending on which date is supplied.
 * @param {string} startDate  startDate should be in the format of 'YYYY-MM-DD'
 * @param {string} endDate endDate should be in the format of 'YYYY-MM-DD'
 * @param {string} precision how precise the comparison should be.  Valid values are 'year', 'month', 'day', 'hour', 'minute', 'second'
 * @returns true if the current date is withing the supplied dates, false otherwise
 */
export const isDateWithinRange = (startDate, endDate, precision = "day") => {
  if (startDate && endDate) {
    return isDateBetween(startDate, endDate, precision);
  } else if (startDate) {
    return isDateOnOrAfter(startDate, precision);
  } else if (endDate) {
    return isDateAfter(endDate, precision) ? false : true;
  }
  return false;
};
